import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type Guardian = {
  id: number
  full_name: string
  relationship: string
  relationship_display: string
  is_primary: boolean
  phone: string
  email: string | null
}

type InsuranceCoverage = {
  id: number
  payer_detail: { id: number; name: string; payer_id: string; payer_type: string }
  member_id: string
  group_number: string | null
  is_primary: boolean
  is_active: boolean
  verification_status: string
  last_verified_date: string | null
}

type ClientDetail = {
  id: number
  full_name: string
  date_of_birth: string
  age: number
  medical_record_number: string
  status: string
  status_display: string
  gender_display: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip_code: string
  primary_phone: string
  secondary_phone: string | null
  email: string | null
  primary_diagnosis_code: string
  primary_diagnosis_description: string
  guardians: Guardian[]
  insurance_coverages: InsuranceCoverage[]
  created_at: string
  updated_at: string
}

type SessionSummary = {
  id: number
  service_date: string
  service_code: string
  status: string
  status_display: string
  scheduled_start: string
  scheduled_end: string
  billable_units: string | null
  note_status: string
}

export default function ClientDetailPage() {
  const tenantSlug = resolveTenantSlug()
  const params = useParams()
  const clientId = Number(params.clientId)

  const clientCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:clients:detail:${clientId}`
  }, [clientId, tenantSlug])

  const sessionsCacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:clients:${clientId}:sessions`
  }, [clientId, tenantSlug])

  const clientQuery = useOfflineFirstQuery<ClientDetail>({
    queryKey: ['client', clientId],
    cacheKey: clientCacheKey,
    cacheMaxAgeMs: 15 * 60 * 1000,
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      const res = await apiClient.get<ClientDetail>(`/api/v1/clients/${clientId}/`)
      return res.data
    },
  })

  const sessionsQuery = useOfflineFirstQuery<SessionSummary[]>({
    queryKey: ['client', clientId, 'sessions'],
    cacheKey: sessionsCacheKey,
    cacheMaxAgeMs: 15 * 60 * 1000,
    enabled: Number.isFinite(clientId) && clientId > 0,
    queryFn: async () => {
      const res = await apiClient.get<SessionSummary[]>(`/api/v1/clients/${clientId}/sessions/`)
      return res.data
    },
  })

  const sessionRows = sessionsQuery.data ?? []

  if (!Number.isFinite(clientId) || clientId <= 0) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        Invalid client id.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {clientQuery.data?.full_name ?? 'Client'}
            </h1>
            {clientQuery.data?.status_display && (
              <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                {clientQuery.data.status_display}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">
            MRN: {clientQuery.data?.medical_record_number ?? '-'} | DOB:{' '}
            {clientQuery.data?.date_of_birth ?? '-'} | Age: {clientQuery.data?.age ?? '-'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/app/clients" className="btn-secondary">
            Back
          </Link>
          <Link to={`/app/sessions/new?client=${clientId}`} className="btn-primary">
            New session
          </Link>
        </div>
      </div>

      {clientQuery.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(clientQuery.error)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Demographics</h2>
            {clientQuery.isLoading && !clientQuery.data ? (
              <div className="space-y-3">
                <div className="h-4 w-72 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-80 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : clientQuery.data ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Gender</div>
                  <div className="text-gray-900">{clientQuery.data.gender_display}</div>
                </div>
                <div>
                  <div className="text-gray-500">Primary diagnosis</div>
                  <div className="text-gray-900">
                    {clientQuery.data.primary_diagnosis_code} -{' '}
                    {clientQuery.data.primary_diagnosis_description}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="text-gray-500">Address</div>
                  <div className="text-gray-900">
                    {clientQuery.data.address_line1}
                    {clientQuery.data.address_line2 ? `, ${clientQuery.data.address_line2}` : ''}
                    , {clientQuery.data.city}, {clientQuery.data.state} {clientQuery.data.zip_code}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Primary phone</div>
                  <div className="text-gray-900">{clientQuery.data.primary_phone}</div>
                </div>
                <div>
                  <div className="text-gray-500">Email</div>
                  <div className="text-gray-900">{clientQuery.data.email ?? '-'}</div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">No client data.</div>
            )}
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Session History</h2>
              <Link
                to={`/app/sessions?client=${clientId}`}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                View all
              </Link>
            </div>

            {sessionsQuery.isError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 mb-4">
                {getErrorMessage(sessionsQuery.error)}
              </div>
            )}

            {sessionsQuery.isLoading && sessionRows.length === 0 ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <div key={idx} className="h-12 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            ) : sessionRows.length === 0 ? (
              <div className="text-sm text-gray-600">No sessions found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="table-header">Date</th>
                      <th className="table-header">Service</th>
                      <th className="table-header">Status</th>
                      <th className="table-header">Note</th>
                      <th className="table-header">Units</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {sessionRows.slice(0, 10).map((s) => (
                      <tr key={s.id} className="hover:bg-gray-50">
                        <td className="table-cell">
                          <Link
                            to={`/app/sessions/${s.id}`}
                            className="font-medium text-gray-900 hover:text-primary-600"
                          >
                            {s.service_date}
                          </Link>
                        </td>
                        <td className="table-cell">{s.service_code}</td>
                        <td className="table-cell">{s.status_display ?? s.status}</td>
                        <td className="table-cell">{s.note_status}</td>
                        <td className="table-cell">{s.billable_units ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Guardians</h2>
            {clientQuery.isLoading && !clientQuery.data ? (
              <div className="space-y-2">
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
                <div className="h-10 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : (clientQuery.data?.guardians?.length ?? 0) === 0 ? (
              <div className="text-sm text-gray-600">No guardians on file.</div>
            ) : (
              <div className="space-y-3">
                {clientQuery.data?.guardians.map((g) => (
                  <div key={g.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-gray-900">{g.full_name}</div>
                        <div className="text-xs text-gray-500">
                          {g.relationship_display || g.relationship}
                        </div>
                      </div>
                      {g.is_primary && (
                        <span className="inline-flex items-center rounded-md bg-primary-50 border border-primary-200 px-2 py-1 text-xs font-medium text-primary-700">
                          Primary
                        </span>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      <div>{g.phone}</div>
                      {g.email ? <div>{g.email}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Insurance</h2>
            {clientQuery.isLoading && !clientQuery.data ? (
              <div className="space-y-2">
                <div className="h-14 bg-gray-200 rounded animate-pulse" />
                <div className="h-14 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : (clientQuery.data?.insurance_coverages?.length ?? 0) === 0 ? (
              <div className="text-sm text-gray-600">No coverage on file.</div>
            ) : (
              <div className="space-y-3">
                {clientQuery.data?.insurance_coverages.map((c) => (
                  <div key={c.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium text-gray-900">{c.payer_detail.name}</div>
                        <div className="text-xs text-gray-500">
                          Member ID: {c.member_id}
                          {c.group_number ? ` | Group: ${c.group_number}` : ''}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {c.is_primary && (
                          <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                            Primary
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${
                            c.is_active
                              ? 'bg-green-50 border-green-200 text-green-800'
                              : 'bg-gray-50 border-gray-200 text-gray-700'
                          }`}
                        >
                          {c.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-600">
                      Verification: {c.verification_status}
                      {c.last_verified_date ? ` | Last verified: ${c.last_verified_date}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
